import { useTranslation } from "react-i18next";
import { Bug, Smartphone, ClipboardList, TrendingDown, Package } from "lucide-react";
import { useInView } from "@/hooks/useInView";

/* ── Illustrations ── */
import { CropIntelligenceIllustration } from "./illustrations/CropIntelligenceIllustration";
import { FieldOpsIllustration } from "./illustrations/FieldOpsIllustration";
import { TreatmentRecordsIllustration } from "./illustrations/TreatmentRecordsIllustration";
import { CostIntelligenceIllustration } from "./illustrations/CostIntelligenceIllustration";
import { InventoryAutomationIllustration } from "./illustrations/InventoryAutomationIllustration";

function FeatureRow({ icon: Icon, badge, title, desc, illustration, reverse, index }: {
  icon: React.ElementType;
  badge: string;
  title: string;
  desc: string;
  illustration: React.ReactNode;
  reverse?: boolean;
  index: number;
}) {
  const { ref, inView } = useInView({ threshold: 0.15 });

  return (
    <div
      ref={ref}
      className={`flex flex-col items-center gap-10 lg:gap-16 transition-all duration-550 ${
        reverse ? "lg:flex-row-reverse" : "lg:flex-row"
      }`}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? "translateY(0)" : "translateY(30px)",
      }}
    >
      <div className="flex-1 space-y-4">
        <div className="flex items-center gap-2">
          <Icon className="h-5 w-5 text-[hsl(213,70%,51%)]" />
          <span className="text-sm font-semibold bg-gradient-to-r from-[hsl(177,58%,52%)] to-[hsl(213,70%,51%)] bg-clip-text text-transparent">{badge}</span>
        </div>
        <h3 className="font-heading text-2xl font-bold text-foreground lg:text-3xl">{title}</h3>
        <p className="max-w-lg text-base leading-relaxed text-muted-foreground lg:text-lg">{desc}</p>
      </div>
      <div className="w-full flex-1 lg:max-w-md">{illustration}</div>
    </div>
  );
}

/* ── Main section ── */
export function DetailedFeatures() {
  const { t } = useTranslation();
  const { ref: headerRef, inView: headerInView } = useInView();

  const features = [
    {
      icon: Bug,
      badge: t("landing.detailed.cropIntel.badge", "Crop Intelligence"),
      title: t("landing.detailed.cropIntel.title", "Detect problems before they spread"),
      desc: t("landing.detailed.cropIntel.desc", "Ulture analyzes field activity, treatment history and crop conditions to detect rising pest pressure early. Get automatic alerts when something unusual appears between blocks, crops or cycles — so you can act before it becomes a problem."),
      illustration: <CropIntelligenceIllustration />,
    },
    {
      icon: Smartphone,
      badge: t("landing.detailed.fieldOps.badge", "Field Operations"),
      title: t("landing.detailed.fieldOps.title", "Your team works from the field, not the office"),
      desc: t("landing.detailed.fieldOps.desc", "Workers see tasks directly on their phones, record scouting sessions and log treatments in seconds — even with limited connectivity. Everything that happens in the field is captured instantly."),
      illustration: <FieldOpsIllustration />,
      reverse: true,
    },
    {
      icon: ClipboardList,
      badge: t("landing.detailed.records.badge", "Treatment Records"),
      title: t("landing.detailed.records.title", "Every treatment, fully traceable"),
      desc: t("landing.detailed.records.desc", "All treatments are logged automatically with product, dose, location and operator. Generate complete spray logs and treatment history instantly. Ready for audits, certifications and inspections."),
      illustration: <TreatmentRecordsIllustration />,
    },
    {
      icon: TrendingDown,
      badge: t("landing.detailed.costIntel.badge", "Cost Intelligence"),
      title: t("landing.detailed.costIntel.title", "Understand treatment costs instantly"),
      desc: t("landing.detailed.costIntel.desc", "See the real cost per hectare, per crop and per block. Ulture connects treatments, inventory and operations to show where money is actually going."),
      illustration: <CostIntelligenceIllustration />,
      reverse: true,
    },
    {
      icon: Package,
      badge: t("landing.detailed.inventory.badge", "Inventory Automation"),
      title: t("landing.detailed.inventory.title", "Your stock updates itself"),
      desc: t("landing.detailed.inventory.desc", "Every treatment automatically updates product inventory. Know what you have, what you're consuming and what needs to be restocked before work stops."),
      illustration: <InventoryAutomationIllustration />,
    },
  ];

  return (
    <section className="relative overflow-hidden bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-6xl px-4">
        {/* Header */}
        <div
          ref={headerRef}
          className="mb-16 text-center lg:mb-20 transition-all duration-500"
          style={{
            opacity: headerInView ? 1 : 0,
            transform: headerInView ? "translateY(0)" : "translateY(20px)",
          }}
        >
          <span className="mb-4 inline-block rounded-full border border-[hsl(213,70%,51%)]/30 bg-[hsl(213,70%,51%)]/10 px-4 py-1.5 text-xs font-semibold tracking-[0.2em] uppercase bg-gradient-to-r from-[hsl(177,58%,52%)] to-[hsl(213,70%,51%)] bg-clip-text text-transparent">
            {t("landing.detailed.badge", "Deep Dive")}
          </span>
          <h2 className="mx-auto max-w-3xl text-3xl lg:text-5xl font-black text-landing-text">
            {t("landing.detailed.title", "Built for the way greenhouses actually work")}
          </h2>
        </div>

        {/* Feature rows */}
        <div className="space-y-20 lg:space-y-28">
          {features.map((f, i) => (
            <FeatureRow key={i} {...f} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
